import { RoutingConfig } from '@nexelem/navigation';
import { once } from 'lodash';
import { lazy, LazyExoticComponent } from 'react';
import {
  createBrowserRouter,
  createRoutesFromElements,
  LayoutRouteProps,
  Navigate,
  Route,
  RouteProps,
} from 'react-router-dom';
import { CommonLoader } from '../components/atoms/CommonLoader';
import NotFoundPage from '../components/organisms/NotFoundPage';
import { AuthContextData } from '../providers/AuthProvider';
import { DefaultApi as GraphneApi } from '@nexelem/graphne-api';
import { DefaultApi as NexelemApi } from '@nexelem/api';
import { ROUTES } from './config';
import { RouteDepsProps } from './generic';
import { getPage, getRouting } from './utils';
import { ClientId } from '@nexelem/config';
import { createBrowserRouterWithFaro } from '@nexelem/grafana';

type LoadedRouting = {
  getRouteConfig: (deps: RouteDepsProps) => RoutingConfig[];
  LoginPage: LazyExoticComponent<any>;
};

type ClientRoutes = {
  getRouter(
    authContextData: AuthContextData,
    api: NexelemApi,
    graphneApi: GraphneApi,
    clientId: ClientId,
  ): {
    routeConfig: RoutingConfig[];
    browserRouter: ReturnType<typeof createBrowserRouter>;
    defaultPath: string;
  };
};

const getFirstAvailablePath = (routeConfig: RoutingConfig[]): string => {
  const firstPath = routeConfig.find((_) => _.props.path);
  if (firstPath?.props.path) {
    return firstPath.props.path;
  }
  return '/';
};

const MainLayout = lazy(() => import('../pages/generic/MainLayout'));

const getRouteConfig = once((clientId: ClientId) => {
  const routeConfigMap = {
    [clientId]: import('./generic'),
    [ClientId.defaultClient]: import('./generic'),
    [ClientId.espro]: import('./clients/espro'),
    [ClientId.siap]: import('./clients/siap'),
    [ClientId.etyflex]: import('./clients/etyflex'),
    [ClientId.sanok]: import('./clients/sanok'),
    [ClientId.ekoplast]: import('./clients/ekoplast'),
    [ClientId.nanochem]: import('./clients/nanochem'),
  };

  return routeConfigMap[clientId];
});
export const loadRoutes = async (clientId: ClientId): Promise<ClientRoutes> => {
  const defaultConfig = {
    getRouteConfig: () => [],
    LoginPage: lazy(() => import('../pages/generic/LoginPage')),
  };
  const router: LoadedRouting = {
    ...defaultConfig,
    ...(await getRouteConfig(clientId)).default,
  };
  return {
    getRouter(
      { user, hasPermission, userState }: AuthContextData,
      api: NexelemApi,
      graphneApi: GraphneApi,
      clientId: ClientId,
    ) {
      if (userState.isLoading) {
        return {
          routeConfig: [],
          defaultPath: '/',
          browserRouter: createBrowserRouterWithFaro([
            {
              element: <CommonLoader />,
              path: '*',
            },
          ]),
        };
      }

      const { getRouteConfig, LoginPage } = router;
      if (user) {
        const routeConfig = getRouteConfig({ hasPermission, api, graphneApi, clientId });

        const routing = getRouting(routeConfig);
        const firstAvailablePath = getFirstAvailablePath(routeConfig);
        return {
          routeConfig,
          defaultPath: firstAvailablePath,
          browserRouter: createBrowserRouterWithFaro(
            createRoutesFromElements(
              <>
                <Route {...(getPage(false, { isProtected: true, id: 'layout' }, MainLayout) as LayoutRouteProps)}>
                  {routing}
                  <Route
                    path="*"
                    {...getPage(
                      false,
                      {
                        isProtected: false,
                        id: '404',
                        title: 'routing.utils.notFoundPage',
                      },
                      NotFoundPage,
                    )}
                  />
                </Route>
                <Route
                  {...(getPage(
                    false,
                    {
                      isProtected: false,
                      id: 'login',
                      path: ROUTES.login,
                    },
                    LoginPage,
                  ) as RouteProps)}
                />
                <Route element={<Navigate replace to={firstAvailablePath} />} path="" />
              </>,
            ),
          ),
        };
      }

      return {
        routeConfig: [],
        defaultPath: '/',
        browserRouter: createBrowserRouterWithFaro(
          createRoutesFromElements(
            <>
              <Route
                {...(getPage(
                  false,
                  {
                    isProtected: false,
                    id: 'login',
                    path: ROUTES.login,
                  },
                  LoginPage,
                ) as RouteProps)}
              />
              ,
              <Route path="*" {...getPage(false, { isProtected: true }, () => <Navigate replace to="/login" />)} />
            </>,
          ),
        ),
      };
    },
  };
};
